import React, { useState } from 'react';
import { Table, Card, Button, Space, message, DatePicker, Row, Col, Statistic, Typography } from 'antd';
import { DownloadOutlined, ReloadOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Title } from '../../components/design-system/typography';
import { StatusPill, getStatusVariant } from '../../components/design-system/StatusPill';
import { colors, spacing } from '../../components/design-system/tokens';
import { reportsApi } from '../../services/api';
import dayjs, { Dayjs } from 'dayjs';

const { RangePicker } = DatePicker;
const { Text } = Typography;

interface OutstandingShipment {
  id: string;
  reference: string;
  contractRef: string;
  bookingNumber: string | null;
  etd: string | null;
  eta: string | null;
  status: string;
  totalLbs: number;
  containerCount: number;
}

interface SupplierGroup {
  supplierId: string;
  supplierName: string;
  supplierCode: string;
  shipmentCount: number;
  totalLbs: number;
  containerCount: number;
  earliestEta: string | null;
  latestEta: string | null;
  shipments: OutstandingShipment[];
}

interface ReportResponse {
  suppliers: SupplierGroup[];
  totals: {
    supplierCount: number;
    shipmentCount: number;
    totalLbs: number;
    containerCount: number;
  };
}

const OutstandingBySupplier: React.FC = () => {
  const navigate = useNavigate();
  const [dateRange, setDateRange] = useState<[Dayjs | null, Dayjs | null] | null>(null);
  const [expandedRowKeys, setExpandedRowKeys] = useState<string[]>([]);

  const { data, isLoading, refetch } = useQuery<ReportResponse>({
    queryKey: ['outstanding-by-supplier', dateRange],
    queryFn: async () => {
      const params: { etaFrom?: string; etaTo?: string } = {};
      if (dateRange?.[0]) params.etaFrom = dateRange[0].format('YYYY-MM-DD');
      if (dateRange?.[1]) params.etaTo = dateRange[1].format('YYYY-MM-DD');
      return reportsApi.getOutstandingBySupplier(params);
    },
  });

  const handleExport = async () => {
    try {
      const token = localStorage.getItem('atlas_token') || localStorage.getItem('auth_token');
      const response = await fetch('/api/v1/export/excel', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          reportType: 'outstanding-by-supplier',
          filters: {},
        }),
      });

      if (!response.ok) throw new Error('Export failed');

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `outstanding-by-supplier-${dayjs().format('YYYY-MM-DD')}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      message.success('Report exported successfully');
    } catch (error) {
      message.error('Failed to export report');
    }
  };

  const formatNumber = (num: number) => num.toLocaleString();
  const formatDate = (date: string | null) => date ? dayjs(date).format('MMM D, YYYY') : '-';

  const supplierColumns: ColumnsType<SupplierGroup> = [
    {
      title: 'Supplier',
      dataIndex: 'supplierName',
      key: 'supplierName',
      sorter: (a, b) => a.supplierName.localeCompare(b.supplierName),
      render: (name, record) => (
        <Space>
          <Text strong>{name}</Text>
          {record.supplierCode && <Text type="secondary">({record.supplierCode})</Text>}
        </Space>
      ),
    },
    {
      title: 'Shipments',
      dataIndex: 'shipmentCount',
      key: 'shipmentCount',
      align: 'right',
      sorter: (a, b) => a.shipmentCount - b.shipmentCount,
      defaultSortOrder: 'descend',
    },
    {
      title: 'Containers',
      dataIndex: 'containerCount',
      key: 'containerCount',
      align: 'right',
      sorter: (a, b) => a.containerCount - b.containerCount,
    },
    {
      title: 'Total Lbs',
      dataIndex: 'totalLbs',
      key: 'totalLbs',
      align: 'right',
      sorter: (a, b) => a.totalLbs - b.totalLbs,
      render: (lbs) => formatNumber(Math.round(lbs)),
    },
    {
      title: 'Earliest ETA',
      dataIndex: 'earliestEta',
      key: 'earliestEta',
      render: formatDate,
      sorter: (a, b) => {
        if (!a.earliestEta) return 1;
        if (!b.earliestEta) return -1;
        return a.earliestEta.localeCompare(b.earliestEta);
      },
    },
    {
      title: 'Latest ETA',
      dataIndex: 'latestEta',
      key: 'latestEta',
      render: formatDate,
    },
  ];

  const shipmentColumns: ColumnsType<OutstandingShipment> = [
    {
      title: 'Contract Ref',
      dataIndex: 'contractRef',
      key: 'contractRef',
      render: (ref) => (
        <a onClick={() => navigate(`/contracts/${ref}`)} style={{ cursor: 'pointer' }}>
          {ref}
        </a>
      ),
    },
    {
      title: 'Shipment',
      dataIndex: 'reference',
      key: 'reference',
    },
    {
      title: 'Booking #',
      dataIndex: 'bookingNumber',
      key: 'bookingNumber',
      render: (val) => val || '-',
    },
    {
      title: 'ETD',
      dataIndex: 'etd',
      key: 'etd',
      render: formatDate,
    },
    {
      title: 'ETA',
      dataIndex: 'eta',
      key: 'eta',
      render: formatDate,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => <StatusPill variant={getStatusVariant(status)} size="small">{status}</StatusPill>,
    },
    {
      title: 'Lbs',
      dataIndex: 'totalLbs',
      key: 'totalLbs',
      align: 'right',
      render: (lbs) => formatNumber(Math.round(lbs)),
    },
    {
      title: 'Containers',
      dataIndex: 'containerCount',
      key: 'containerCount',
      align: 'right',
    },
  ];

  const expandedRowRender = (record: SupplierGroup) => (
    <Table
      columns={shipmentColumns}
      dataSource={record.shipments}
      rowKey="id"
      pagination={false}
      size="small"
      style={{ margin: '0 16px' }}
    />
  );

  return (
    <div style={{ padding: spacing['2xl'], background: colors.background.primary, minHeight: '100vh' }}>
      <div style={{ maxWidth: 1400, margin: '0 auto' }}>
        <div style={{ marginBottom: spacing.xl, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <Title>Outstanding Shipments by Supplier</Title>
            <Text type="secondary">
              Shipments that have not yet arrived or been delivered, grouped by supplier
            </Text>
          </div>
          <Space>
            <Button icon={<ReloadOutlined />} onClick={() => refetch()}>
              Refresh
            </Button>
            <Button type="primary" icon={<DownloadOutlined />} onClick={handleExport}>
              Export to Excel
            </Button>
          </Space>
        </div>

        <Card style={{ marginBottom: spacing.lg }}>
          <Row gutter={16} align="middle">
            <Col>
              <Text strong>ETA Date Range:</Text>
            </Col>
            <Col>
              <RangePicker
                value={dateRange}
                onChange={(dates) => setDateRange(dates as [Dayjs | null, Dayjs | null] | null)}
                allowClear
                style={{ width: 280 }}
              />
            </Col>
          </Row>
        </Card>

        <Row gutter={16} style={{ marginBottom: spacing.lg }}>
          <Col span={6}>
            <Card>
              <Statistic
                title="Total Suppliers"
                value={data?.totals?.supplierCount || 0}
                valueStyle={{ color: colors.primary[60] }}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="Total Shipments"
                value={data?.totals?.shipmentCount || 0}
                valueStyle={{ color: colors.status.warning }}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="Total Containers"
                value={data?.totals?.containerCount || 0}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="Total Weight"
                value={formatNumber(Math.round(data?.totals?.totalLbs || 0))}
                suffix="lbs"
              />
            </Card>
          </Col>
        </Row>

        <Card>
          <Table
            columns={supplierColumns}
            dataSource={data?.suppliers || []}
            rowKey="supplierId"
            loading={isLoading}
            expandable={{
              expandedRowRender,
              expandedRowKeys,
              onExpandedRowsChange: (keys) => setExpandedRowKeys(keys as string[]),
            }}
            pagination={{
              pageSize: 20,
              showSizeChanger: true,
              showTotal: (total) => `${total} suppliers`,
            }}
          />
        </Card>
      </div>
    </div>
  );
};

export default OutstandingBySupplier;
